import React, { useEffect, useMemo } from 'react';
import { getAnalytics, logEvent } from 'firebase/analytics';
import { DeepLinkTabs } from '../../utils/deepLink/DeepLinkTabs';
import { useDeepLinkParam } from '../../utils/deepLink/useDeepLinkSegment';
import { useDispatch, useSelector } from 'react-redux';
import { Flex, Item, Picker, ProgressCircle, Section, TabList, TabPanels, Text, View } from '@adobe/react-spectrum';
import { SearedLoader } from '../genericLoadingDialog/SearedLoader';
import FlashOn from '@spectrum-icons/workflow/FlashOn';
import DataMapping from '@spectrum-icons/workflow/DataMapping';
import ColorPalette from '@spectrum-icons/workflow/ColorPalette';
import HotFixes from '@spectrum-icons/workflow/HotFixes';
import {
    buildBinCacheKey,
    DEFAULT_BIN_KEY,
    fetchCommanderStatsHealth,
    selectCommanderBin,
} from '../../data/redux/slices/commanderCardStatsSlice';
import { CardsTab } from './commanderCardStatsPanes/CardsTab';
import { CombosTab } from './commanderCardStatsPanes/CombosTab';
import { SynergyTab } from './commanderCardStatsPanes/SynergyTab';
import { ManabaseTab } from './commanderCardStatsPanes/ManabaseTab';
import { ArchetypesTab } from './commanderCardStatsPanes/ArchetypesTab';
import { SaltTab } from './commanderCardStatsPanes/SaltTab';
import './CommanderCardStatsPanel.css';
import { useOpenCardInfo } from '../common/cardInfoDialog/useOpenCardInfo';
import Card from '@spectrum-icons/workflow/Card';
import Beaker from '@spectrum-icons/workflow/Beaker';
import { ARCHETYPE_ANY, prettyStatsMajor, prettyStatsSub } from '../../data/statsArchetypeTaxonomy';

const BIN_SECTIONS = [
    {
        id: 'brackets',
        name: 'Bracket',
        children: [
            { key: 'bracket_1', label: 'Bracket 1' },
            { key: 'bracket_2', label: 'Bracket 2' },
            { key: 'bracket_3', label: 'Bracket 3' },
            { key: 'bracket_4', label: 'Bracket 4' },
            { key: 'bracket_5', label: 'Bracket 5' },
        ],
    },
    {
        id: 'power',
        name: 'Power Level',
        children: [
            { key: 'power_1_3', label: 'Power 1–3' },
            { key: 'power_3_5', label: 'Power 3–5' },
            { key: 'power_5_7', label: 'Power 5–7' },
            { key: 'power_7_9', label: 'Power 7–9' },
            { key: 'power_9_10', label: 'Power 9–10' },
        ],
    },
];

// Surface a low-sample warning when fewer than this many decks fall into
// the active (bin × archetype) cell — at that point the percentages are
// noisy enough to mislead users.
const LOW_SAMPLE_THRESHOLD = 30;

const bracketFromRating = (rating) => {
    if (rating == null) return null;
    const rounded = Math.round(parseFloat(rating));
    if (Number.isNaN(rounded)) return null;
    const clamped = Math.max(1, Math.min(5, rounded));
    return `bracket_${clamped}`;
};

// Format a deckCount as a compact suffix for dropdown labels — e.g. "12".
const fmtCount = (n) => (typeof n === 'number' && Number.isFinite(n) ? n.toLocaleString() : '—');

export function CommanderCardStatsPanel() {
    const dispatch = useDispatch();

    const commanderIndexId = useSelector((state) => state.commanderDetails?.details?.id);
    const bracketRating = useSelector((state) => state.commanderDetails?.details?.bracketRating);

    const selectedBinKey = useSelector((state) => state.commanderCardStats.selectedBinKey);
    const selectedArchetype = useSelector((state) => state.commanderCardStats.selectedArchetype);
    const selectedSubArchetype = useSelector((state) => state.commanderCardStats.selectedSubArchetype);
    const responsesByBin = useSelector((state) => state.commanderCardStats.responsesByBin);
    const fetchingBins = useSelector((state) => state.commanderCardStats.fetchingBins);
    const isDisabled = useSelector((state) => state.commanderCardStats.isDisabled);
    const storedCommanderId = useSelector((state) => state.commanderCardStats.commanderIndexId);
    const health = useSelector((state) =>
        commanderIndexId ? state.commanderCardStats.healthById[commanderIndexId] : null
    );
    const healthIsFetching = useSelector((state) =>
        commanderIndexId ? !!state.commanderCardStats.healthFetching[commanderIndexId] : false
    );

    const handleSelectCard = useOpenCardInfo();

    const smartDefaultBin = bracketFromRating(bracketRating) || DEFAULT_BIN_KEY;
    // Unscoped: the panel is wrapped in a mobile tab layout but not a
    // desktop one; keep the `bin` URL identical across viewports.
    const [binParam, setBinParam] = useDeepLinkParam('bin', smartDefaultBin, { scope: false });
    const [archetypeParam, setArchetypeParam] = useDeepLinkParam('arch', ARCHETYPE_ANY, { scope: false });
    const [subArchetypeParam, setSubArchetypeParam] = useDeepLinkParam('sub', ARCHETYPE_ANY, { scope: false });

    // Health probe first — tells us which bins have data and whether the
    // commander has been aggregated at all. All bin fetches are gated on this.
    useEffect(() => {
        if (commanderIndexId) dispatch(fetchCommanderStatsHealth(commanderIndexId));
    }, [commanderIndexId, dispatch]);

    const availableBins = health?.availableBins || {};
    // The API marks Dynamo-read degradation separately from "no data" so
    // we can render a non-blocking warning without nuking on-screen state.
    const healthReadStatus = health?.healthReadStatus || 'ok';
    const aggregationStatus = health?.status; // 'ready' | 'stale' | 'pending' | 'below_threshold' | 'disabled'

    const availableBinSections = useMemo(() => {
        if (!health) return BIN_SECTIONS;
        return BIN_SECTIONS.map((section) => ({
            ...section,
            children: section.children
                .filter((child) => {
                    const info = availableBins[child.key];
                    return info && (info.deckCount ?? 0) > 0;
                })
                .map((child) => ({ ...child, deckCount: availableBins[child.key]?.deckCount ?? 0 })),
        })).filter((section) => section.children.length > 0);
    }, [health, availableBins]);

    const availableBinKeys = useMemo(() => {
        const keys = new Set();
        availableBinSections.forEach((s) => s.children.forEach((c) => keys.add(c.key)));
        return keys;
    }, [availableBinSections]);

    // Snap URL/selection to a valid bin once health lands. If the URL's bin
    // isn't in availableBins, prefer the rating-derived bracket, else fall
    // back to other brackets, else the first available key.
    const effectiveBin = useMemo(() => {
        if (!health || availableBinKeys.size === 0) return null;
        if (availableBinKeys.has(binParam)) return binParam;
        const preferred = [smartDefaultBin, 'bracket_4', 'bracket_5', 'bracket_3', 'bracket_2', 'bracket_1', 'overall'];
        return preferred.find((k) => availableBinKeys.has(k)) || availableBinSections[0]?.children[0]?.key || null;
    }, [health, availableBinKeys, binParam, smartDefaultBin, availableBinSections]);

    const healthReady =
        !!health &&
        aggregationStatus !== 'disabled' &&
        aggregationStatus !== 'pending' &&
        aggregationStatus !== 'below_threshold' &&
        health.eligible;

    // ---- Archetype availability tree (per-bin) ----
    // The health endpoint returns the actual majors/subs present for the
    // selected bracket/power; we use that as the source of truth for
    // dropdown options (per ARCHETYPE_BINS_API_CHANGES.md §Health endpoint).
    // The static taxonomy in statsArchetypeTaxonomy.js is the validation
    // universe — we never need to consult it here.
    const archetypesForBin = useMemo(() => {
        if (!effectiveBin) return {};
        return availableBins[effectiveBin]?.archetypes || {};
    }, [availableBins, effectiveBin]);

    const archetypeMajorItems = useMemo(() => {
        const entries = Object.entries(archetypesForBin)
            .filter(([, info]) => info && (info.deckCount ?? 0) > 0)
            .sort((a, b) => (b[1].deckCount || 0) - (a[1].deckCount || 0))
            .map(([id, info]) => ({
                key: id,
                label: `${prettyStatsMajor(id)} (${fmtCount(info.deckCount)})`,
            }));
        return [{ key: ARCHETYPE_ANY, label: 'Any archetype' }, ...entries];
    }, [archetypesForBin]);

    // Sub-archetype is scoped to its major AND to the current bin's
    // availability tree — fall back to ANY when the selection isn't
    // emitted for this (bin, major) cell.
    const effectiveArchetype = useMemo(() => {
        const picked = archetypeParam || ARCHETYPE_ANY;
        if (picked === ARCHETYPE_ANY) return ARCHETYPE_ANY;
        return archetypesForBin[picked] ? picked : ARCHETYPE_ANY;
    }, [archetypeParam, archetypesForBin]);

    const subsForMajor = useMemo(() => {
        if (!effectiveArchetype || effectiveArchetype === ARCHETYPE_ANY) return {};
        return archetypesForBin[effectiveArchetype]?.subArchetypes || {};
    }, [archetypesForBin, effectiveArchetype]);

    const effectiveSubArchetype = useMemo(() => {
        if (effectiveArchetype === ARCHETYPE_ANY) return ARCHETYPE_ANY;
        if (!subArchetypeParam || subArchetypeParam === ARCHETYPE_ANY) return ARCHETYPE_ANY;
        return subsForMajor[subArchetypeParam] ? subArchetypeParam : ARCHETYPE_ANY;
    }, [effectiveArchetype, subArchetypeParam, subsForMajor]);

    const subArchetypeItems = useMemo(() => {
        const entries = Object.entries(subsForMajor)
            .filter(([, info]) => info && (info.deckCount ?? 0) > 0)
            .sort((a, b) => (b[1].deckCount || 0) - (a[1].deckCount || 0))
            .map(([id, info]) => ({
                key: id,
                label: `${prettyStatsSub(id)} (${fmtCount(info.deckCount)})`,
            }));
        return [{ key: ARCHETYPE_ANY, label: 'Any sub' }, ...entries];
    }, [subsForMajor]);

    // Keep URL clean: when the persisted archetype/sub snaps out of
    // availability (bin change, partial rebuild), drop the URL param.
    useEffect(() => {
        if (!healthReady) return;
        if (effectiveArchetype !== archetypeParam) setArchetypeParam(effectiveArchetype);
    }, [healthReady, effectiveArchetype, archetypeParam, setArchetypeParam]);
    useEffect(() => {
        if (!healthReady) return;
        if (effectiveSubArchetype !== subArchetypeParam) setSubArchetypeParam(effectiveSubArchetype);
    }, [healthReady, effectiveSubArchetype, subArchetypeParam, setSubArchetypeParam]);

    const apiArchetype = effectiveArchetype === ARCHETYPE_ANY ? null : effectiveArchetype;
    const apiSub = effectiveSubArchetype === ARCHETYPE_ANY ? null : effectiveSubArchetype;

    // Dispatch bin fetch only when health is ready and we have a valid bin.
    useEffect(() => {
        if (!commanderIndexId || !healthReady || !effectiveBin) return;
        const binMatches = selectedBinKey === effectiveBin;
        const archMatches = (selectedArchetype || null) === apiArchetype;
        const subMatches = (selectedSubArchetype || null) === apiSub;
        if (storedCommanderId !== commanderIndexId || !binMatches || !archMatches || !subMatches) {
            dispatch(selectCommanderBin(commanderIndexId, effectiveBin, apiArchetype, apiSub));
        }
    }, [
        commanderIndexId,
        storedCommanderId,
        selectedBinKey,
        selectedArchetype,
        selectedSubArchetype,
        effectiveBin,
        apiArchetype,
        apiSub,
        healthReady,
        dispatch,
    ]);

    // Keep the URL param in sync when we snap to a different bin.
    useEffect(() => {
        if (!effectiveBin) return;
        if (binParam !== effectiveBin) setBinParam(effectiveBin);
    }, [effectiveBin, binParam, setBinParam]);

    const handleBinChange = (key) => {
        if (!commanderIndexId) return;
        if (!availableBinKeys.has(key)) return;
        logEvent(getAnalytics(), `commander_setBin_${String(key).toUpperCase()}`);
        // Bin change can invalidate the active archetype — re-derive
        // against the new bin's availability tree before dispatching so
        // we never send a (bin, archetype) pair the API would 400 on.
        const nextBinArchetypes = availableBins[key]?.archetypes || {};
        const nextMajor = apiArchetype && nextBinArchetypes[apiArchetype] ? apiArchetype : null;
        const nextSubMap = nextMajor ? nextBinArchetypes[nextMajor]?.subArchetypes || {} : {};
        const nextSub = nextMajor && apiSub && nextSubMap[apiSub] ? apiSub : null;
        setBinParam(key);
        dispatch(selectCommanderBin(commanderIndexId, key, nextMajor, nextSub));
    };

    const handleArchetypeChange = (key) => {
        if (!commanderIndexId) return;
        const nextMajor = key || ARCHETYPE_ANY;
        logEvent(getAnalytics(), `commander_setArchetype_${String(nextMajor).toUpperCase()}`);
        setArchetypeParam(nextMajor);
        // Drop the sub unless it's still emitted under the new major
        // for the current bin.
        const nextSubMap = nextMajor === ARCHETYPE_ANY ? {} : archetypesForBin[nextMajor]?.subArchetypes || {};
        const nextSub = subArchetypeParam && nextSubMap[subArchetypeParam] ? subArchetypeParam : ARCHETYPE_ANY;
        if (nextSub !== subArchetypeParam) setSubArchetypeParam(nextSub);
        const apiMajorNext = nextMajor === ARCHETYPE_ANY ? null : nextMajor;
        const apiSubNext = nextSub === ARCHETYPE_ANY ? null : nextSub;
        dispatch(selectCommanderBin(commanderIndexId, effectiveBin, apiMajorNext, apiSubNext));
    };

    const handleSubArchetypeChange = (key) => {
        if (!commanderIndexId) return;
        const nextSub = key || ARCHETYPE_ANY;
        logEvent(getAnalytics(), `commander_setSubArchetype_${String(nextSub).toUpperCase()}`);
        setSubArchetypeParam(nextSub);
        const apiSubNext = nextSub === ARCHETYPE_ANY ? null : nextSub;
        dispatch(selectCommanderBin(commanderIndexId, effectiveBin, apiArchetype, apiSubNext));
    };

    const cacheKey = buildBinCacheKey(selectedBinKey, apiArchetype, apiSub);
    const response = responsesByBin[cacheKey];
    const isFetching = Boolean(fetchingBins[cacheKey]);
    const bin = response?.bin || null;

    const pickerSections = availableBinSections.length > 0 ? availableBinSections : BIN_SECTIONS;
    const pickerDisabled = !healthReady;
    // Aggregation can be `stale` during a rebuild — bins still serve data
    // but archetype rollups may be partial. Surface a tooltip on the
    // archetype dropdown rather than disabling it outright.
    const archetypeDisabled = pickerDisabled || archetypeMajorItems.length <= 1;
    const archetypeTooltip = (() => {
        if (aggregationStatus === 'stale') return 'Stats are rebuilding — archetype options may be partial.';
        if (archetypeMajorItems.length <= 1) return 'No archetype data for this bracket yet.';
        return undefined;
    })();

    // Pull the active bin's deckCount from the availability tree so the
    // low-sample badge can render even before the bin response lands.
    const activeBinDeckCount =
        apiArchetype && apiSub
            ? archetypesForBin[apiArchetype]?.subArchetypes?.[apiSub]?.deckCount
            : apiArchetype
            ? archetypesForBin[apiArchetype]?.deckCount
            : availableBins[effectiveBin]?.deckCount;

    // Mobile-only collapsible filter row. Desktop renders pickers inline as before.
    const binLabelByKey = useMemo(() => {
        const map = {};
        BIN_SECTIONS.forEach((s) => s.children.forEach((c) => (map[c.key] = c.label)));
        return map;
    }, []);
    const summaryBinLabel = binLabelByKey[selectedBinKey] || binLabelByKey[effectiveBin] || '—';
    const summaryArchetypeLabel =
        effectiveArchetype && effectiveArchetype !== ARCHETYPE_ANY ? prettyStatsMajor(effectiveArchetype) : null;
    const summarySubLabel =
        effectiveSubArchetype && effectiveSubArchetype !== ARCHETYPE_ANY ? prettyStatsSub(effectiveSubArchetype) : null;
    const filterSummary = [summaryBinLabel, summaryArchetypeLabel, summarySubLabel].filter(Boolean).join(' · ');

    const [filtersOpen, setFiltersOpen] = React.useState(false);

    const renderShell = (body) => (
        <View UNSAFE_className="commanderCardStatsPanel" padding={{ base: '8px', S: '20px' }}>
            <Flex direction="column" rowGap={15}>
                <div className="commanderCardStatsHeader">
                    <Text UNSAFE_className="commanderCardStatsTitle">Deck composition stats</Text>
                    <button
                        type="button"
                        className="commanderCardStatsFiltersToggle"
                        aria-expanded={filtersOpen}
                        aria-controls="commanderCardStatsFiltersBody"
                        onClick={() => setFiltersOpen((v) => !v)}
                    >
                        <span className="commanderCardStatsFiltersToggleLabel">
                            <span className="commanderCardStatsFiltersToggleKey">Filters:</span>{' '}
                            <span className="commanderCardStatsFiltersToggleValue">{filterSummary}</span>
                        </span>
                        <span className="commanderCardStatsFiltersToggleChevron" aria-hidden="true">
                            {filtersOpen ? '▾' : '▸'}
                        </span>
                    </button>
                    <div
                        id="commanderCardStatsFiltersBody"
                        className={`commanderCardStatsFilters${filtersOpen ? ' is-open' : ''}`}
                    >
                        <Picker
                            label="Show stats for"
                            selectedKey={selectedBinKey}
                            onSelectionChange={handleBinChange}
                            items={pickerSections}
                            width={{ base: '100%', S: 200 }}
                            isDisabled={pickerDisabled}
                        >
                            {(section) => (
                                <Section key={section.id} items={section.children} title={section.name}>
                                    {(option) => <Item key={option.key}>{option.label}</Item>}
                                </Section>
                            )}
                        </Picker>
                        <div title={archetypeTooltip} className="commanderCardStatsFiltersPickerWrap">
                            <Picker
                                label="Archetype"
                                selectedKey={effectiveArchetype}
                                onSelectionChange={(key) => handleArchetypeChange(String(key))}
                                items={archetypeMajorItems}
                                width={{ base: '100%', S: 200 }}
                                isDisabled={archetypeDisabled}
                            >
                                {(item) => <Item key={item.key}>{item.label}</Item>}
                            </Picker>
                        </div>
                        <Picker
                            label="Sub-archetype"
                            selectedKey={effectiveSubArchetype}
                            onSelectionChange={(key) => handleSubArchetypeChange(String(key))}
                            items={subArchetypeItems}
                            width={{ base: '100%', S: 210 }}
                            isDisabled={pickerDisabled || !apiArchetype || subArchetypeItems.length <= 1}
                        >
                            {(item) => <Item key={item.key}>{item.label}</Item>}
                        </Picker>
                    </div>
                </div>
                {healthReadStatus === 'degraded' && (
                    <Text UNSAFE_className="commanderCardStatsDegraded">
                        Stats data is temporarily degraded — figures may load incomplete. Retry in a few seconds.
                    </Text>
                )}
                {body}
            </Flex>
        </View>
    );

    if (!commanderIndexId) return null;

    // Health probe hasn't landed yet.
    if (!health) {
        return renderShell(<SearedLoader size="large" subcaption={healthIsFetching ? 'Checking availability…' : ''} />);
    }

    if (health.status === 'disabled' || isDisabled) {
        return renderShell(
            <Text UNSAFE_className="commanderCardStatsEmpty">
                Deck composition stats are temporarily unavailable. Check back soon.
            </Text>
        );
    }

    if (health.status === 'pending') {
        return renderShell(
            <SearedLoader
                size="large"
                caption="Crunching the numbers"
                subcaption="Stats are being computed for this commander. Check back in a few minutes."
            />
        );
    }

    if (health.status === 'below_threshold' || !health.eligible) {
        return renderShell(
            <Text UNSAFE_className="commanderCardStatsEmpty">
                {`Not enough decks yet to compute stats — we need at least ${
                    health.threshold || 50
                } decks for this commander.`}
            </Text>
        );
    }

    if (availableBinKeys.size === 0) {
        return renderShell(<Text UNSAFE_className="commanderCardStatsEmpty">No bins with enough data yet.</Text>);
    }

    if (isFetching && !response) {
        return renderShell(<SearedLoader size="large" subcaption="Loading deck composition stats…" />);
    }

    if (!response || !bin) {
        return null;
    }

    // Prefer the live bin response's deckCount; fall back to the
    // availability tree (handy during the cache-bump rebuild window when
    // the bin payload may briefly lag).
    const liveDeckCount = bin.deckCount ?? activeBinDeckCount ?? 0;
    const sampleSizeLabel =
        liveDeckCount > 0
            ? `${liveDeckCount.toLocaleString()} deck${liveDeckCount === 1 ? '' : 's'} analyzed in this bucket`
            : 'No decks in this bucket yet';
    const lowSample = liveDeckCount > 0 && liveDeckCount < LOW_SAMPLE_THRESHOLD;

    return renderShell(
        <>
            <Flex direction="row" alignItems="center" columnGap={10} rowGap={6} wrap>
                <Text UNSAFE_className="commanderCardStatsSampleSize">{sampleSizeLabel}</Text>
                {lowSample && (
                    <span
                        className="commanderCardStatsLowSampleBadge"
                        title={`Fewer than ${LOW_SAMPLE_THRESHOLD} decks in this slice — percentages may be noisy.`}
                    >
                        Low sample
                    </span>
                )}
                {aggregationStatus === 'stale' && (
                    <span
                        className="commanderCardStatsStaleBadge"
                        title="Stats are being rebuilt — figures may shift over the next few minutes."
                    >
                        Rebuilding
                    </span>
                )}
                {isFetching && <ProgressCircle size="S" aria-label="Refreshing" isIndeterminate />}
            </Flex>
            {liveDeckCount === 0 ? (
                <Text UNSAFE_className="commanderCardStatsEmpty">Nothing to show yet.</Text>
            ) : (
                <DeepLinkTabs
                    defaultSelectedKey="cards"
                    isEmphasized
                    UNSAFE_style={{ padding: 0 }}
                    onSelectionChange={(key) =>
                        logEvent(getAnalytics(), `commander_setSubTab_${String(key).toUpperCase()}`)
                    }
                >
                    <TabList marginTop="20px" marginStart="10px">
                        <Item key="archetypes">
                            <HotFixes size={'XS'} />
                            Archetypes
                        </Item>
                        <Item key="cards">
                            <Card size={'XS'} />
                            Cards
                        </Item>
                        <Item key="combos">
                            <FlashOn size={'XS'} />
                            Combos
                        </Item>
                        <Item key="manabase">
                            <ColorPalette size={'XS'} />
                            Manabase
                        </Item>
                        <Item key="salt">
                            <Beaker size={'XS'} />
                            Saltiness
                        </Item>
                        <Item key="synergy">
                            <DataMapping size={'XS'} />
                            Synergy
                        </Item>
                    </TabList>
                    <TabPanels marginTop="10px">
                        <Item key="archetypes">
                            <ArchetypesTab bin={bin} onSelectCard={handleSelectCard} />
                        </Item>
                        <Item key="cards">
                            <CardsTab bin={bin} onSelectCard={handleSelectCard} />
                        </Item>
                        <Item key="combos">
                            <CombosTab bin={bin} onSelectCard={handleSelectCard} />
                        </Item>
                        <Item key="manabase">
                            <ManabaseTab bin={bin} onSelectCard={handleSelectCard} />
                        </Item>
                        <Item key="salt">
                            <SaltTab bin={bin} onSelectCard={handleSelectCard} />
                        </Item>
                        <Item key="synergy">
                            <SynergyTab bin={bin} onSelectCard={handleSelectCard} />
                        </Item>
                    </TabPanels>
                </DeepLinkTabs>
            )}
        </>
    );
}
